<script setup lang="ts">
import { tracking } from '~/services/tracking.client'

import type { ProductListInterface } from "~~/server/types/Products";
import { rememberProductListPosition } from "~/composables/useProductListPosition";

const props = defineProps<{
    list: ProductListInterface[];
}>();

const { t } = useI18n();

const rememberProductFromClick = (
    event: MouseEvent,
    item: ProductListInterface
) => {
    const target = event.target;

    if (
        target instanceof Element &&
        target.closest("[data-product-link]")
    ) {
        tracking.selectItem(item, trackingListId(), props.list.findIndex(p => p.id === item.id));
        rememberProductListPosition(item.id);
    }
};
const trackingRoute = useRoute()
const trackingListId = () => `products:${trackingRoute.path}`
onMounted(() => {
    let previousList = ''
    watch(() => props.list, list => {
        const key = list.map(item => item.id).join(',')
        if (key !== previousList) {
            previousList = key
            tracking.viewItemList(list, trackingListId())
        }
    }, { immediate: true })
})
</script>

<template>
    <div class="row">
        <div
            v-for="item in props.list"
            :key="item.id"
            :data-product-id="item.id"
            class="col-lg-3 col-md-6 col-12 mb-lg-0 mb-4"
        >
            <div
                class="simple-grids__card"
                @click.capture="rememberProductFromClick($event, item)"
            >
                <NuxtLinkLocale
                    :to="item.url"
                    class="simple-grids__media w-100"
                    data-product-link
                    aria-label="View product details"
                >
                    <img
                        class="simple-grids__img"
                        :src="item.thumb"
                        :alt="item.name"
                        width="400"
                        height="300"
                        loading="lazy"
                        decoding="async"
                    >
                </NuxtLinkLocale>

                <h3 class="simple-grids__card-title pt-3 text-center mt-2">
                    {{ item.name }}
                </h3>

                <NuxtLinkLocale
                    :to="item.url"
                    class="simple-grids__btn mt-2"
                    data-product-link
                    aria-label="View product details"
                >
                    <span
                        class="simple-grids__btn-icon"
                        data-disabled="true"
                    >
                        <span
                            class="simple-grids__btn-dot"
                            data-disabled="true"
                        />
                    </span>

                    <span class="simple-grids__btn-text">
                        {{ t("view_product") }}
                    </span>

                    <span
                        class="simple-grids__btn-arrow"
                        data-disabled="true"
                    >
                        →
                    </span>
                </NuxtLinkLocale>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.simple-grids__card {
    background-color: #F8F8F8;
    border-radius: 20px;
    flex-direction: column;
    align-items: center;
    gap: 0;
    padding-bottom: 1rem;
    text-align: center;
}

.simple-grids__media {
    background-color: var(--el-color-primary);
    color: #fff;
    padding-bottom: 1px;
}

.simple-grids__img {
    width: 100%;
    aspect-ratio: 4 / 3;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
    height: auto;
}

.simple-grids__card-title {
    font-size: 1.2rem;
    font-weight: 500;
}

.simple-grids__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;

    padding: 12px 28px;

    background: #f7f9fb;
    border-radius: 8px;

    color: inherit;
    text-decoration: none;

    transition: all 0.3s ease;
}

.simple-grids__btn-icon {
    position: relative;

    width: 28px;
    height: 28px;
    margin-right: 10px;

    display: flex;
    align-items: center;
    justify-content: center;
}

.simple-grids__btn-dot {
    width: 12px;
    height: 12px;

    background-color: var(--el-color-primary);
    border-radius: 50%;

    z-index: 2;
    position: relative;
}

.simple-grids__btn-icon::before {
    content: "";

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    border: 3px solid #1a1a1a;
    border-radius: 50%;
    box-sizing: border-box;

    clip-path: polygon(
        0 0,
        100% 0,
        100% 35%,
        70% 35%,
        70% 65%,
        100% 65%,
        100% 100%,
        0 100%
    );

    transform: rotate(0deg);
    transition: transform 0.6s ease;
}

.simple-grids__btn:hover .simple-grids__btn-icon::before {
    transform: rotate(360deg);
}

.simple-grids__btn-text {
    font-size: 1.05rem;
    font-weight: 600;
}

.simple-grids__btn-arrow {
    margin-left: 8px;
    font-size: 1.3rem;

    transition: transform 0.3s ease;
}

.simple-grids__btn:hover .simple-grids__btn-arrow {
    transform: translateX(4px);
}

@media (max-width: 992px) {
    .simple-grids__card-title {
        font-size: 1.25rem;
        font-weight: 500;
    }

    .simple-grids__btn-text {
        font-size: 1rem;
        font-weight: 600;
    }

    .simple-grids__btn-icon {
        width: 24px;
        height: 24px;
    }

    .simple-grids__btn-dot {
        width: 10px;
        height: 10px;
    }

    .simple-grids__btn {
        padding: 8px 25px;
    }
}
</style>